import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface Breadcrumb {
  label?: string;
  alias?: string;
  url: string;
  avatarDocId?: string;
  avatarName?: string;
  icon?: string;
  translate?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const breadcrumbs = this.buildBreadcrumbs(this.route.root);
        this._breadcrumbs$.next(breadcrumbs);
      });
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    // Preserve the last known state so that we don't lose labels/icons that were
    // supplied dynamically (e.g. after the component finished loading) when the
    // router fires again for a pure query-param navigation.
    const prevBreadcrumbs = this._breadcrumbs$.getValue();

    for (const child of children) {
      const routeUrl = child.snapshot.url
        .map(segment => segment.path)
        .join('/');
      const newUrl = routeUrl ? `${url}/${routeUrl}` : url;

      const breadcrumbData = child.snapshot.data['breadcrumb'];
      if (breadcrumbData) {
        const exists = breadcrumbs.some(b => b.url === newUrl);
        if (!exists) {
          // Try to find a previously stored breadcrumb with the same alias so
          // we can keep any runtime updates (e.g. label coming from API).
          const previous = prevBreadcrumbs.find(
            b =>
              b.alias &&
              breadcrumbData.alias &&
              b.alias === breadcrumbData.alias
          );

          breadcrumbs.push({
            label: breadcrumbData.label ?? previous?.label,
            alias: breadcrumbData.alias,
            url: newUrl,
            icon: breadcrumbData.icon ?? previous?.icon,
            translate: breadcrumbData.translate,
          });
        }
      }
      this.buildBreadcrumbs(child, newUrl, breadcrumbs);
    }

    return breadcrumbs;
  }

  updateBreadcrumbLabel(alias: string, newLabel: string): void {
    const breadcrumbs = this._breadcrumbs$.getValue().map(breadcrumb => {
      if (breadcrumb.alias === alias) {
        return { ...breadcrumb, label: newLabel };
      }
      return breadcrumb;
    });

    this._breadcrumbs$.next(breadcrumbs);
  }

  updateBreadcrumbIcon(alias: string, icon: string): void {
    const breadcrumbs = this._breadcrumbs$.getValue().map(breadcrumb => {
      if (breadcrumb.alias === alias) {
        return { ...breadcrumb, icon };
      }
      return breadcrumb;
    });
    this._breadcrumbs$.next(breadcrumbs);
  }

  updateBreadcrumbAvatar(
    alias: string,
    avatarDocId?: string,
    avatarName?: string
  ): void {
    const breadcrumbs = this._breadcrumbs$.getValue().map(breadcrumb => {
      if (breadcrumb.alias === alias) {
        return { ...breadcrumb, avatarDocId, avatarName };
      }
      return breadcrumb;
    });

    this._breadcrumbs$.next(breadcrumbs);
  }

  setCustomBreadcrumbs(breadcrumbs: Breadcrumb[]): void {
    this._breadcrumbs$.next(breadcrumbs);
  }

  clearBreadcrumbs(): void {
    this._breadcrumbs$.next([]);
  }
}
